/**
 * Engine wrapper — creates the Babylon.js Engine and a base Scene with sensible defaults.
 *
 * Handles canvas acquisition, antialiasing, and window resize.
 */
import { Engine as BabylonEngine } from "@babylonjs/core/Engines/engine";
import { AbstractEngine } from "@babylonjs/core/Engines/abstractEngine";
// Registers AbstractEngine.AudioEngineFactory (needed for `audioEngine: true`).
import "@babylonjs/core/Audio/audioEngine";
import { Scene } from "@babylonjs/core/scene";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { SceneLoaderFlags } from "@babylonjs/core/Loading/sceneLoaderFlags";
import "@babylonjs/core/Audio/audioSceneComponent";
// Required for scene.beginAnimation — patches Scene.prototype via AddAnimationExtensions(Scene, Bone)
import "@babylonjs/core/Animations/animatable";
// Required for AdvancedDynamicTexture (GUI) — ensures DynamicTexture is registered on the engine
import "@babylonjs/core/Materials/Textures/dynamicTexture";
// Required if Babylon's default scene loader UI is ever enabled.
import "@babylonjs/core/Loading/loadingScreen";
// Inspector — lazy-loaded in dev mode only
import "@babylonjs/core/Debug/debugLayer";

import { isLowFiMode, isSoftwareRenderer } from "./lowFiMode";

// The game owns its loading UI through AssetManager. Babylon's default scene
// loader overlay conflicts with that custom screen and can throw if its side
// effect has not been imported before an async GLB load starts.
SceneLoaderFlags.ShowLoadingScreen = false;

export interface EngineOptions {
  canvasId: string;
  antialias: boolean;
}

const DEFAULT_OPTIONS: EngineOptions = {
  canvasId: "renderCanvas",
  antialias: true,
};

/**
 * Hard ceiling for `engine.getDeltaTime()` in milliseconds.
 *
 * 33 ms = 30 Hz floor. When the page stalls (tab backgrounded, GC
 * pause, off-frame GLB load) the browser can hand Babylon a single
 * delta of hundreds of milliseconds, which multiplies every velocity
 * integration downstream (abilities, ConservatoryCycle lerps, NPC
 * patrol drift). Clamping here means the game time-dilates on stalls
 * rather than producing catastrophic single-frame jumps that drive a
 * cat through the floor.
 *
 * Consumed by 16+ files via `engine.getDeltaTime()` /
 * `scene.getEngine().getDeltaTime()` — the wrapper in `GameEngine`
 * installs a clamp once at construction so every consumer inherits it
 * without per-site changes.
 */
export const MAX_DELTA_MS = 33;

/** Fixed simulation step (seconds) for gameplay and physics. */
export const SIM_STEP_SECONDS = 1 / 60;

/**
 * Decide whether the engine must disable uniform buffers.
 *
 * Software GL stacks (SwiftShader in headless Chromium / CI containers,
 * Mesa llvmpipe) expose as few as 14 vertex uniform buffer bindings.
 * Babylon's PBR + clustered-light shaders exceed that, every material
 * compile fails, and Babylon retries the failing effect each frame —
 * measured in-container as ~30 shader-compile errors per second, 215 of
 * 237 materials permanently not-ready. With UBOs disabled, Babylon
 * falls back to classic uniforms (limited by maxVertexUniformVectors,
 * typically 4096 — plenty) and everything compiles.
 *
 * Auto-detects software renderers (shared probe in core/lowFiMode.ts);
 * the `?qaDisableUbo=1|0` query param force-overrides in either
 * direction (used by the QA harness and useful for debugging).
 */
export function shouldDisableUniformBuffers(): boolean {
  try {
    const override = new URLSearchParams(window.location.search).get("qaDisableUbo");
    if (override === "1") return true;
    if (override === "0") return false;
  } catch {
    // fall through to auto-detection
  }
  return isSoftwareRenderer();
}

/**
 * Internal-resolution divisor for low-fi mode. 3 turns a 1280x720
 * canvas into a ~427x240 framebuffer — fragment cost drops ~9x on a
 * CPU rasterizer while HUD text (drawn by Babylon GUI into the same
 * buffer) stays readable.
 */
const LOW_FI_HARDWARE_SCALING = 3;

export class GameEngine {
  public engine: BabylonEngine;
  public scene: Scene;
  public canvas: HTMLCanvasElement;

  constructor(options?: Partial<EngineOptions>) {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    const canvas = document.getElementById(opts.canvasId) as HTMLCanvasElement | null;
    if (!canvas) {
      throw new Error(`Canvas element with id "${opts.canvasId}" not found.`);
    }
    this.canvas = canvas;

    this.engine = new BabylonEngine(canvas, opts.antialias, {
      preserveDrawingBuffer: false,
      stencil: true,
      // Without this Babylon 9 creates no audio engine and every legacy
      // `new Sound()` silently returns early — the game shipped silent.
      audioEngine: true,
      // Fixed-step simulation: gameplay + physics advance in exact 1/60 s
      // steps (BaseLevel runs its game loop on onBeforeStepObservable), so
      // movement, jump arcs and stopping distances are identical at 30,
      // 60 or 144 Hz and runs replay deterministically. Rendering stays
      // per-frame; cat visuals are interpolated between steps.
      deterministicLockstep: true,
      lockstepMaxSteps: 4,
      timeStep: SIM_STEP_SECONDS,
    }, true /* adaptToDeviceRatio — critical for Retina/HiDPI sharpness */);
    installAudioUnlock();

    // Must be set on the engine FIELD (it is not a creation option) and
    // before the first material compiles — see shouldDisableUniformBuffers.
    if (shouldDisableUniformBuffers()) {
      console.warn(
        "[Cats of Chronos] Software GL renderer detected — disabling " +
          "uniform buffers so PBR shaders can compile " +
          "(see shouldDisableUniformBuffers).",
      );
      this.engine.disableUniformBuffers = true;
    }

    // Minimum-spec profile: drop the internal render resolution. The rest
    // of low-fi mode (no shadows/glow/post-process/accent lights) is
    // applied by applyVisualProfile + setupBaseLevel — see core/lowFiMode.
    if (isLowFiMode()) {
      console.warn(
        "[Cats of Chronos] Low-fi rendering mode active (software GL " +
          "detected or ?lofi=1) — reduced resolution, no shadows/glow/" +
          "post-processing. Gameplay is unaffected.",
      );
      this.engine.setHardwareScalingLevel(LOW_FI_HARDWARE_SCALING);
    }

    // Clamp `getDeltaTime()` to MAX_DELTA_MS so downstream consumers
    // (abilities, NPC AI, ConservatoryCycle lerps, ability cooldowns)
    // never integrate a catastrophic frame delta when the page stalls.
    // See the MAX_DELTA_MS doc comment for rationale.
    const originalGetDeltaTime = this.engine.getDeltaTime.bind(this.engine);
    this.engine.getDeltaTime = () => Math.min(originalGetDeltaTime(), MAX_DELTA_MS);

    // Scene constructor options — large-scene lookup optimizations.
    // Reference: https://doc.babylonjs.com/features/featuresDeepDive/scene/optimize_your_scene
    // Each flag builds an extra index map so mesh/material membership
    // checks are O(1) instead of O(n). Trades a small amount of memory
    // for major wins on a 1500+ mesh level.
    this.scene = new Scene(this.engine, {
      useGeometryUniqueIdsMap: true,
      useMaterialMeshMap: true,
      useClonedMeshMap: true,
    });

    // Handle window resize
    window.addEventListener("resize", () => {
      this.engine.resize();
    });

    // Toggle Babylon.js Inspector in dev mode: Shift+Ctrl+Alt+I
    if (import.meta.env.DEV) {
      window.addEventListener("keydown", async (ev) => {
        if (ev.shiftKey && ev.ctrlKey && ev.altKey && ev.key === "I") {
          const { Inspector } = await import("@babylonjs/inspector");
          if (Inspector.IsVisible) {
            Inspector.Hide();
          } else {
            Inspector.Show(this.scene, {});
          }
        }
      });
    }
  }

  /** Start the render loop. */
  startRenderLoop(): void {
    this.engine.runRenderLoop(() => {
      this.scene.render();
    });
  }

  /**
   * Dispose the current scene and replace it with a fresh one.
   * The render loop continues — it reads this.scene dynamically.
   * A temporary camera is added so there's no "No camera defined" error
   * during the frames before the next screen creates its own camera.
   */
  replaceScene(): void {
    this.scene.dispose();
    // Preserve the same large-scene lookup optimizations as the initial
    // scene so level transitions don't regress their mesh/material
    // membership lookup speed.
    this.scene = new Scene(this.engine, {
      useGeometryUniqueIdsMap: true,
      useMaterialMeshMap: true,
      useClonedMeshMap: true,
    });
    // Temporary camera — prevents "No camera defined" error in the render
    // loop during the frames before the next screen's setup completes.
    const tempCam = new FreeCamera("_tempCam", new Vector3(0, 0, -10), this.scene);
    tempCam.setTarget(Vector3.Zero());
  }

  /** Stop the render loop and dispose resources. */
  dispose(): void {
    this.engine.stopRenderLoop();
    this.scene.dispose();
    this.engine.dispose();
  }
}

/**
 * Browsers keep the AudioContext suspended until a user gesture. Suppress
 * Babylon's default unmute overlay button and unlock on the first key,
 * pointer or touch instead (the title screen asks for exactly that).
 */
function installAudioUnlock(): void {
  const audioEngine = AbstractEngine.audioEngine;
  if (!audioEngine || typeof window === "undefined") return;
  audioEngine.useCustomUnlockedButton = true;
  const unlock = (): void => {
    audioEngine.unlock();
    window.removeEventListener("keydown", unlock, true);
    window.removeEventListener("pointerdown", unlock, true);
    window.removeEventListener("touchstart", unlock, true);
  };
  window.addEventListener("keydown", unlock, true);
  window.addEventListener("pointerdown", unlock, true);
  window.addEventListener("touchstart", unlock, true);
}
